import { useCallback, useEffect, useState } from 'react';

import { getBscRpcUrlOrDefault } from '~/config/bridgeConfig';

type EthereumProvider = {
	request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
	on?: (event: string, handler: (...args: unknown[]) => void) => void;
	removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
	isMetaMask?: boolean;
};

declare global {
	interface Window {
		ethereum?: EthereumProvider;
	}
}

const BSC_MAINNET_HEX = '0x38';
const BSC_TESTNET_HEX = '0x61';

function getProvider(): EthereumProvider | undefined {
	const eth = window.ethereum;
	if (!eth) return undefined;
	const providers = (eth as EthereumProvider & { providers?: EthereumProvider[] }).providers?.filter(
		Boolean,
	);
	if (providers?.length) {
		return providers.find((p) => p.isMetaMask) ?? providers[0];
	}
	return eth;
}

async function ethRequest<T>(method: string, params?: unknown[]): Promise<T> {
	const provider = getProvider();
	if (!provider) throw new Error('未检测到 MetaMask 或其它 EVM 钱包');
	return (await provider.request({ method, params })) as T;
}

async function switchBscChain(useTestnet: boolean): Promise<void> {
	const chainIdHex = useTestnet ? BSC_TESTNET_HEX : BSC_MAINNET_HEX;
	try {
		await ethRequest('wallet_switchEthereumChain', [{ chainId: chainIdHex }]);
	} catch (e) {
		const err = e as { code?: number };
		if (err.code !== 4902) throw e;
		await ethRequest('wallet_addEthereumChain', [
			useTestnet
				? {
						chainId: BSC_TESTNET_HEX,
						chainName: 'BSC Testnet',
						nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
						rpcUrls: ['https://data-seed-prebsc-1-s1.bnbchain.org:8545'],
						blockExplorerUrls: ['https://testnet.bscscan.com'],
					}
				: {
						chainId: BSC_MAINNET_HEX,
						chainName: 'BNB Smart Chain',
						nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
						rpcUrls: [getBscRpcUrlOrDefault()],
						blockExplorerUrls: ['https://bscscan.com'],
					},
		]);
	}
}

/** ERC-20 balanceOf(address) — returns raw uint256 hex. */
async function fetchErc20Balance(
	rpcUrl: string,
	token: string,
	owner: string,
): Promise<bigint> {
	const data =
		'0x70a08231' +
		owner.slice(2).padStart(64, '0').toLowerCase();
	const res = await fetch(rpcUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			jsonrpc: '2.0',
			id: 1,
			method: 'eth_call',
			params: [{ to: token, data }, 'latest'],
		}),
	});
	const json = (await res.json()) as { result?: string; error?: { message: string } };
	if (json.error) throw new Error(json.error.message);
	if (!json.result || json.result === '0x') return 0n;
	return BigInt(json.result);
}

function formatTokenBalance(raw: bigint, decimals: number): string {
	const base = 10n ** BigInt(decimals);
	const whole = raw / base;
	const frac = raw % base;
	if (frac === 0n) return whole.toString();
	const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '');
	return `${whole}.${fracStr}`;
}

export function useBscWallet(options: {
	useTestnet: boolean;
	usdtToken: string;
	usdtDecimals: number;
	bscRpcUrl?: string;
}) {
	const { useTestnet, usdtToken, usdtDecimals, bscRpcUrl } = options;
	const [address, setAddress] = useState<string | null>(null);
	const [usdtBalance, setUsdtBalance] = useState<string | null>(null);
	const [connecting, setConnecting] = useState(false);
	const [chainOk, setChainOk] = useState<boolean | null>(null);

	const refreshUsdtBalance = useCallback(async () => {
		if (!address || !bscRpcUrl) {
			setUsdtBalance(null);
			return;
		}
		try {
			const raw = await fetchErc20Balance(bscRpcUrl, usdtToken, address);
			setUsdtBalance(formatTokenBalance(raw, usdtDecimals));
		} catch {
			setUsdtBalance(null);
		}
	}, [address, bscRpcUrl, usdtToken, usdtDecimals]);

	const connect = useCallback(async () => {
		setConnecting(true);
		try {
			await switchBscChain(useTestnet);
			const accounts = await ethRequest<string[]>('eth_requestAccounts');
			const acct = accounts[0] ?? null;
			setAddress(acct);
			const chainId = await ethRequest<string>('eth_chainId');
			const expected = useTestnet ? BSC_TESTNET_HEX : BSC_MAINNET_HEX;
			setChainOk(chainId.toLowerCase() === expected.toLowerCase());
		} finally {
			setConnecting(false);
		}
	}, [useTestnet]);

	const disconnect = useCallback(() => {
		setAddress(null);
		setUsdtBalance(null);
		setChainOk(null);
	}, []);

	useEffect(() => {
		const provider = getProvider();
		if (!provider?.on) return;
		const onAccounts = (accounts: unknown) => {
			const list = accounts as string[];
			setAddress(list[0] ?? null);
		};
		const onChain = (chainId: unknown) => {
			const expected = useTestnet ? BSC_TESTNET_HEX : BSC_MAINNET_HEX;
			setChainOk(String(chainId).toLowerCase() === expected.toLowerCase());
		};
		provider.on('accountsChanged', onAccounts);
		provider.on('chainChanged', onChain);
		return () => {
			provider.removeListener?.('accountsChanged', onAccounts);
			provider.removeListener?.('chainChanged', onChain);
		};
	}, [useTestnet]);

	useEffect(() => {
		void refreshUsdtBalance();
		const t = setInterval(() => void refreshUsdtBalance(), 20_000);
		return () => clearInterval(t);
	}, [refreshUsdtBalance]);

	return {
		address,
		usdtBalance,
		connecting,
		chainOk,
		hasProvider: typeof window !== 'undefined' && !!window.ethereum,
		connect,
		disconnect,
		refreshUsdtBalance,
	};
}
